import { REALITY_FIELD_NAMES } from "../../../smartsetter-utils/filters-dialog/data"
import {
  brandOptionLabel,
  normalizeBrandValue,
} from "../settings/brokerageProfileIntakeChoices"

export const MY_MLS_BOOST_CAMPAIGN_API_PATH = "/crm/api/my-mls-boost-campaign/"
export const MY_MLS_BOOST_CAMPAIGN_MEDIA_API_PATH =
  "/crm/api/my-mls-boost-campaign/media/"

export const BOOST_CAMPAIGN_CRITERIA_STORAGE_KEY = "myMlsBoostCampaignCriteria"

/** Aligned with CRMSettings columns (migration 0048). */
export const BOOST_CAMPAIGN_LIMITS = {
  minRadiusMiles: { min: 0, max: 99_999_999.99, step: 0.01, integer: false },
  maxRadiusMiles: { min: 0, max: 99_999_999.99, step: 0.01, integer: false },
  minSalesUnits: { min: 0, max: 2_147_483_647, step: 1, integer: true },
  maxSalesUnits: { min: 0, max: 2_147_483_647, step: 1, integer: true },
  minSalesProduction: {
    min: 0,
    max: 99_999_999_999_999.99,
    step: 1,
    integer: false,
  },
  maxSalesProduction: {
    min: 0,
    max: 99_999_999_999_999.99,
    step: 1,
    integer: false,
  },
}

export const TARGET_TYPE_OPTIONS = ["Local", "Statewide", "Nationwide"]

/** Read-only on save — media is uploaded via the dedicated media API. */
const READ_ONLY_BOOST_CAMPAIGN_API_FIELDS = ["additionalMediaUrl", "updatedAt"]

export const EMPTY_BOOST_CAMPAIGN_CRITERIA = {
  targetType: "",
  mls: "",
  targetCities: "",
  targetStates: "",
  targetCounties: "",
  minRadiusMiles: "",
  maxRadiusMiles: "",
  brandsToExclude: [],
  officesToExclude: "",
  peopleToExclude: "",
  citiesToExclude: "",
  countiesToExclude: "",
  additionalNote: "",
  additionalMediaUrl: "",
  minSalesUnits: "",
  maxSalesUnits: "",
  minSalesProduction: "",
  maxSalesProduction: "",
}

export function parseCommaSeparatedList(value) {
  if (!value || typeof value !== "string") {
    return []
  }
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
}

export function normalizeBoostCampaignCriteria(criteria) {
  if (!criteria || typeof criteria !== "object") {
    return { ...EMPTY_BOOST_CAMPAIGN_CRITERIA }
  }
  return {
    ...EMPTY_BOOST_CAMPAIGN_CRITERIA,
    ...criteria,
    targetType: (criteria.targetType ?? criteria.target_type ?? "").trim(),
    mls: (criteria.mls ?? criteria.mls_name ?? "").trim(),
    additionalNote: (criteria.additionalNote ?? "").trim(),
    additionalMediaUrl: (
      criteria.additionalMediaUrl ??
      criteria.additional_media_url ??
      ""
    ).trim(),
    brandsToExclude: Array.isArray(criteria.brandsToExclude)
      ? criteria.brandsToExclude
          .map((brand) => normalizeBrandValue(brand) || String(brand).trim())
          .filter(Boolean)
      : [],
  }
}

/** Placeholder for unset values on Settings → Campaign read-only view. */
export const BOOST_CAMPAIGN_UNSET_DISPLAY = "—"

/**
 * Merge MLS fallback (from CRM settings) when saved criteria omit it.
 */
export function enrichBoostCampaignCriteriaForDisplay(
  criteria,
  { mlsFallback = "" } = {}
) {
  const c = normalizeBoostCampaignCriteria(criteria)
  const fallback = (mlsFallback || "").trim()
  return {
    ...c,
    mls: c.mls || fallback,
  }
}

export function loadBoostCampaignCriteria() {
  try {
    const raw = sessionStorage.getItem(BOOST_CAMPAIGN_CRITERIA_STORAGE_KEY)
    if (!raw) {
      return { ...EMPTY_BOOST_CAMPAIGN_CRITERIA }
    }
    return normalizeBoostCampaignCriteria(JSON.parse(raw))
  } catch {
    return { ...EMPTY_BOOST_CAMPAIGN_CRITERIA }
  }
}

export function saveBoostCampaignCriteriaLocal(criteria) {
  sessionStorage.setItem(
    BOOST_CAMPAIGN_CRITERIA_STORAGE_KEY,
    JSON.stringify(normalizeBoostCampaignCriteria(criteria))
  )
}

/** Criteria payload for POST /my-mls-boost-campaign/ (excludes read-only media URL). */
export function buildBoostCampaignCriteriaPayload(criteria) {
  const normalized = normalizeBoostCampaignCriteria(criteria)
  const payload = { ...normalized }
  for (const key of READ_ONLY_BOOST_CAMPAIGN_API_FIELDS) {
    delete payload[key]
  }
  return payload
}

export function mergeBoostCampaignMediaIntoCriteria(criteria, mediaCriteria) {
  const base = normalizeBoostCampaignCriteria(criteria)
  const media = normalizeBoostCampaignCriteria(mediaCriteria)
  return {
    ...base,
    additionalMediaUrl: media.additionalMediaUrl,
  }
}

export function hasBoostCampaignCriteria(criteria) {
  const c = normalizeBoostCampaignCriteria(criteria)
  return (
    Boolean(c.targetType?.trim()) ||
    Boolean(c.mls?.trim()) ||
    Boolean(c.targetCities?.trim()) ||
    Boolean(c.targetStates?.trim()) ||
    Boolean(c.targetCounties?.trim()) ||
    c.minRadiusMiles !== "" ||
    c.maxRadiusMiles !== "" ||
    c.brandsToExclude.length > 0 ||
    Boolean(c.officesToExclude?.trim()) ||
    Boolean(c.peopleToExclude?.trim()) ||
    Boolean(c.citiesToExclude?.trim()) ||
    Boolean(c.countiesToExclude?.trim()) ||
    Boolean(c.additionalNote?.trim()) ||
    Boolean(c.additionalMediaUrl?.trim()) ||
    c.minSalesUnits !== "" ||
    c.maxSalesUnits !== "" ||
    c.minSalesProduction !== "" ||
    c.maxSalesProduction !== ""
  )
}

export function hasBoostCampaignBeenSaved({ criteria, updatedAt } = {}) {
  return Boolean(updatedAt) || hasBoostCampaignCriteria(criteria)
}

function formatDisplayValue(value) {
  if (value === "" || value == null) {
    return null
  }
  return String(value)
}

function formatProductionDisplay(value) {
  const n = parseNumericCriteria(value)
  if (n == null) {
    return null
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n)
}

/**
 * All Lead Targeting form fields in display order (Settings summary + compact views).
 * Keep in sync with BoostLeadsWithCampaignDialog.
 */
export const BOOST_CAMPAIGN_SUMMARY_FIELD_DEFS = [
  { key: "targetType", label: "Target Type" },
  { key: "mls", label: "MLS" },
  { key: "targetCities", label: "Target Cities" },
  { key: "targetStates", label: "Target States" },
  { key: "targetCounties", label: "Target Counties" },
  { key: "minRadiusMiles", label: "Min radius from office address" },
  { key: "maxRadiusMiles", label: "Max radius from office address" },
  { key: "brandsToExclude", label: "Brands to exclude" },
  { key: "officesToExclude", label: "Offices to exclude" },
  { key: "peopleToExclude", label: "People to exclude" },
  { key: "citiesToExclude", label: "Cities to exclude" },
  { key: "countiesToExclude", label: "Counties to exclude" },
  { key: "minSalesUnits", label: "Minimum sales (units #)" },
  { key: "maxSalesUnits", label: "Maximum sales (units #)" },
  { key: "minSalesProduction", label: "Minimum sales (production $)" },
  { key: "maxSalesProduction", label: "Maximum sales (production $)" },
  { key: "additionalNote", label: "Additional note" },
  { key: "additionalMediaUrl", label: "Upload media set" },
]

/** Grouped layout for Settings → Campaign read-only view. */
export const BOOST_CAMPAIGN_DISPLAY_GROUPS = [
  {
    title: "Scope",
    keys: ["targetType", "mls"],
  },
  {
    title: "Geography",
    keys: ["targetCities", "targetStates", "targetCounties"],
  },
  {
    title: "Radius from office",
    keys: ["minRadiusMiles", "maxRadiusMiles"],
  },
  {
    title: "Sales performance",
    keys: [
      "minSalesUnits",
      "maxSalesUnits",
      "minSalesProduction",
      "maxSalesProduction",
    ],
  },
  {
    title: "Exclusions",
    keys: [
      "brandsToExclude",
      "officesToExclude",
      "peopleToExclude",
      "citiesToExclude",
      "countiesToExclude",
    ],
  },
  {
    title: "Additional info",
    keys: ["additionalNote", "additionalMediaUrl"],
  },
]

function formatBoostCampaignFieldValue(key, c) {
  switch (key) {
    case "targetType":
    case "mls":
    case "targetCities":
    case "targetStates":
    case "targetCounties":
    case "officesToExclude":
    case "peopleToExclude":
    case "citiesToExclude":
    case "countiesToExclude":
    case "additionalNote":
      return formatDisplayValue(c[key])
    case "additionalMediaUrl":
      return c.additionalMediaUrl?.trim() ? "Uploaded" : null
    case "minSalesUnits":
    case "maxSalesUnits":
      return formatDisplayValue(c[key])
    case "minRadiusMiles":
    case "maxRadiusMiles":
      return formatDisplayValue(c[key]) ? `${c[key]} mi` : null
    case "brandsToExclude":
      return c.brandsToExclude.length
        ? c.brandsToExclude.map(brandOptionLabel).join(", ")
        : null
    case "minSalesProduction":
    case "maxSalesProduction":
      return formatProductionDisplay(c[key])
    default:
      return null
  }
}

/**
 * Read-only rows for Settings → Campaign tab.
 * @param {object} [options]
 * @param {boolean} [options.showAllFields] When true, list every form field (use — if unset).
 * @param {string} [options.mlsFallback] CRM-selected MLS name(s) when not stored on criteria.
 */
export function getBoostCampaignDisplayRows(
  criteria,
  { showAllFields = false, mlsFallback = "" } = {}
) {
  const c = enrichBoostCampaignCriteriaForDisplay(criteria, { mlsFallback })
  const rows = BOOST_CAMPAIGN_SUMMARY_FIELD_DEFS.map(({ key, label }) => ({
    label,
    value: formatBoostCampaignFieldValue(key, c),
  }))

  if (showAllFields) {
    return rows.map((row) => ({
      ...row,
      value:
        row.value != null && row.value !== ""
          ? row.value
          : BOOST_CAMPAIGN_UNSET_DISPLAY,
    }))
  }

  return rows.filter((row) => row.value != null && row.value !== "")
}

/**
 * Lead targeting fields grouped for the Campaign settings summary.
 */
export function getBoostCampaignDisplayGroups(
  criteria,
  { showAllFields = false, mlsFallback = "" } = {}
) {
  const byLabel = new Map(
    getBoostCampaignDisplayRows(criteria, {
      showAllFields,
      mlsFallback,
    }).map((row) => [row.label, row])
  )
  const defsByKey = Object.fromEntries(
    BOOST_CAMPAIGN_SUMMARY_FIELD_DEFS.map((def) => [def.key, def])
  )

  return BOOST_CAMPAIGN_DISPLAY_GROUPS.map((group) => ({
    title: group.title,
    rows: group.keys
      .map((key) => {
        const def = defsByKey[key]
        if (!def) {
          return null
        }
        return (
          byLabel.get(def.label) ?? {
            label: def.label,
            value: BOOST_CAMPAIGN_UNSET_DISPLAY,
          }
        )
      })
      .filter(Boolean),
  })).filter((group) => group.rows.length > 0)
}

export async function fetchBoostCampaignCriteriaFull(axios) {
  try {
    const { data } = await axios.get(MY_MLS_BOOST_CAMPAIGN_API_PATH)
    return {
      criteria: normalizeBoostCampaignCriteria(data?.criteria),
      updatedAt: data?.updated_at || data?.criteria?.updatedAt || null,
    }
  } catch {
    const local = loadBoostCampaignCriteria()
    return { criteria: local, updatedAt: null }
  }
}

export async function fetchBoostCampaignCriteria(axios) {
  const { criteria } = await fetchBoostCampaignCriteriaFull(axios)
  return criteria
}

export async function saveBoostCampaignCriteriaToApi(
  axios,
  criteria,
  { syncToAirtable = true } = {}
) {
  const payload = {
    ...buildBoostCampaignCriteriaPayload(criteria),
    syncToAirtable,
  }
  const { data } = await axios.post(MY_MLS_BOOST_CAMPAIGN_API_PATH, payload)
  if (data?.criteria) {
    const normalized = normalizeBoostCampaignCriteria(data.criteria)
    saveBoostCampaignCriteriaLocal(normalized)
    return {
      criteria: normalized,
      updatedAt: data.updated_at || data.criteria?.updatedAt || null,
    }
  }
  saveBoostCampaignCriteriaLocal(payload)
  return { criteria: payload, updatedAt: null }
}

/**
 * Map campaign boost form values to CRM My MLS agent list filters.
 * Radius fields are saved with criteria but not sent until the API supports them.
 */
export function boostCampaignCriteriaToAgentFilters(criteria) {
  const filters = []

  const cities = parseCommaSeparatedList(criteria.targetCities)
  const counties = parseCommaSeparatedList(criteria.targetCounties)
  const locationValues = [...new Set([...cities, ...counties])]
  if (locationValues.length) {
    filters.push({
      field: REALITY_FIELD_NAMES.CITY,
      type: "is_one_of",
      value: locationValues,
      _complete: true,
    })
  }

  const states = parseCommaSeparatedList(criteria.targetStates)
  if (states.length) {
    filters.push({
      field: REALITY_FIELD_NAMES.STATE,
      type: "is_one_of",
      value: states,
      _complete: true,
    })
  }

  const excludedBrands = Array.isArray(criteria.brandsToExclude)
    ? criteria.brandsToExclude.filter(Boolean)
    : []
  if (excludedBrands.length) {
    filters.push({
      field: REALITY_FIELD_NAMES.BRAND,
      type: "is_none_of",
      value: excludedBrands,
      _complete: true,
    })
  }

  const excludedOffices = parseCommaSeparatedList(criteria.officesToExclude)
  if (excludedOffices.length) {
    filters.push({
      field: REALITY_FIELD_NAMES.OFFICE_NAME,
      type: "is_none_of",
      value: excludedOffices,
      _complete: true,
    })
  }

  const excludedPeople = parseCommaSeparatedList(criteria.peopleToExclude)
  if (excludedPeople.length) {
    filters.push({
      field: REALITY_FIELD_NAMES.NAME,
      type: "is_none_of",
      value: excludedPeople,
      _complete: true,
    })
  }

  const excludedCities = parseCommaSeparatedList(criteria.citiesToExclude)
  if (excludedCities.length) {
    filters.push({
      field: REALITY_FIELD_NAMES.CITY,
      type: "is_none_of",
      value: excludedCities,
      _complete: true,
    })
  }

  const excludedCounties = parseCommaSeparatedList(criteria.countiesToExclude)
  if (excludedCounties.length) {
    filters.push({
      field: REALITY_FIELD_NAMES.COUNTY,
      type: "is_none_of",
      value: excludedCounties,
      _complete: true,
    })
  }

  const minUnits = parseNumericCriteria(criteria.minSalesUnits)
  if (minUnits != null) {
    filters.push({
      field: REALITY_FIELD_NAMES.NO_OF_SALES,
      type: "gt",
      value: minUnits,
      _complete: true,
    })
  }

  const maxUnits = parseNumericCriteria(criteria.maxSalesUnits)
  if (maxUnits != null) {
    filters.push({
      field: REALITY_FIELD_NAMES.NO_OF_SALES,
      type: "lt",
      value: maxUnits,
      _complete: true,
    })
  }

  const minProduction = parseNumericCriteria(criteria.minSalesProduction)
  if (minProduction != null) {
    filters.push({
      field: REALITY_FIELD_NAMES.SALES_VOLUME,
      type: "gt",
      value: minProduction,
      _complete: true,
    })
  }

  const maxProduction = parseNumericCriteria(criteria.maxSalesProduction)
  if (maxProduction != null) {
    filters.push({
      field: REALITY_FIELD_NAMES.SALES_VOLUME,
      type: "lt",
      value: maxProduction,
      _complete: true,
    })
  }

  return filters
}

function parseNumericCriteria(value) {
  if (value === "" || value == null) {
    return null
  }
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function formatLimitMax(max, integer) {
  if (integer) {
    return max.toLocaleString()
  }
  return max.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function validateNumericField(value, fieldName, limits, label) {
  if (value === "" || value == null) {
    return null
  }
  const raw = String(value).trim()
  if (!raw) {
    return null
  }
  const n = Number(raw)
  if (!Number.isFinite(n)) {
    return `Enter a valid ${label.toLowerCase()}.`
  }
  if (limits.integer && !Number.isInteger(n)) {
    return `${label} must be a whole number.`
  }
  if (n < limits.min) {
    return `${label} cannot be negative.`
  }
  if (n > limits.max) {
    return `${label} must be at most ${formatLimitMax(
      limits.max,
      limits.integer
    )}.`
  }
  return null
}

export function validateBoostCampaignCriteria(criteria) {
  const errors = {}

  const targetType = (criteria.targetType || "").trim()
  if (targetType && !TARGET_TYPE_OPTIONS.includes(targetType)) {
    errors.targetType = "Select Local, Statewide, or Nationwide."
  }

  const minRadiusError = validateNumericField(
    criteria.minRadiusMiles,
    "minRadiusMiles",
    BOOST_CAMPAIGN_LIMITS.minRadiusMiles,
    "Min radius (miles)"
  )
  if (minRadiusError) {
    errors.minRadiusMiles = minRadiusError
  }

  const maxRadiusError = validateNumericField(
    criteria.maxRadiusMiles,
    "maxRadiusMiles",
    BOOST_CAMPAIGN_LIMITS.maxRadiusMiles,
    "Max radius (miles)"
  )
  if (maxRadiusError) {
    errors.maxRadiusMiles = maxRadiusError
  }

  const minUnitsError = validateNumericField(
    criteria.minSalesUnits,
    "minSalesUnits",
    BOOST_CAMPAIGN_LIMITS.minSalesUnits,
    "Minimum sales (units)"
  )
  if (minUnitsError) {
    errors.minSalesUnits = minUnitsError
  }

  const maxUnitsError = validateNumericField(
    criteria.maxSalesUnits,
    "maxSalesUnits",
    BOOST_CAMPAIGN_LIMITS.maxSalesUnits,
    "Maximum sales (units)"
  )
  if (maxUnitsError) {
    errors.maxSalesUnits = maxUnitsError
  }

  const minProductionError = validateNumericField(
    criteria.minSalesProduction,
    "minSalesProduction",
    BOOST_CAMPAIGN_LIMITS.minSalesProduction,
    "Minimum sales (production $)"
  )
  if (minProductionError) {
    errors.minSalesProduction = minProductionError
  }

  const maxProductionError = validateNumericField(
    criteria.maxSalesProduction,
    "maxSalesProduction",
    BOOST_CAMPAIGN_LIMITS.maxSalesProduction,
    "Maximum sales (production $)"
  )
  if (maxProductionError) {
    errors.maxSalesProduction = maxProductionError
  }

  const minRadius = parseNumericCriteria(criteria.minRadiusMiles)
  const maxRadius = parseNumericCriteria(criteria.maxRadiusMiles)
  if (
    minRadius != null &&
    maxRadius != null &&
    minRadius > maxRadius &&
    !errors.maxRadiusMiles
  ) {
    errors.maxRadiusMiles = "Must be greater than or equal to min radius."
  }

  const minUnits = parseNumericCriteria(criteria.minSalesUnits)
  const maxUnits = parseNumericCriteria(criteria.maxSalesUnits)
  if (
    minUnits != null &&
    maxUnits != null &&
    minUnits > maxUnits &&
    !errors.maxSalesUnits
  ) {
    errors.maxSalesUnits = "Maximum must be greater than or equal to minimum."
  }

  const minProduction = parseNumericCriteria(criteria.minSalesProduction)
  const maxProduction = parseNumericCriteria(criteria.maxSalesProduction)
  if (
    minProduction != null &&
    maxProduction != null &&
    minProduction > maxProduction &&
    !errors.maxSalesProduction
  ) {
    errors.maxSalesProduction =
      "Maximum must be greater than or equal to minimum."
  }

  return errors
}

export function numericInputProps(fieldName) {
  const limits = BOOST_CAMPAIGN_LIMITS[fieldName]
  return {
    min: limits.min,
    max: limits.max,
    step: limits.step,
  }
}
